import { notify } from '@/lib/toast';
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Gauge, PlusCircle, Check, X, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { FormDialog } from '@/components/ui/form-dialog';
import { AddonPicker, useDebouncedValue } from '@/components/subscription/AddonPicker';
import { AddonSummary } from '@/components/subscription/AddonSummary';
import { toAddonQuantities, usageWarnings, diffSelection } from '@/lib/addon-basket';
import { AlertBanner } from '@/components/ui/alert-banner';
import { StatCard } from '@/components/ui/stat-card';
import { StatusBadge } from '@/components/ui/status-badge';
import { getTrialPhaseState } from '@/lib/trial';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { formatCurrency, formatDate, formatDateOnly } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import { addonLabel, planLabel } from '@/lib/catalog-labels';
import { IncludedFeatures } from '@/components/subscription/IncludedFeatures';
import { PassThroughNote } from '@/components/subscription/PassThroughNote';

interface UsageMetric {
  used: number;
  max: number | null;
  pct: number | null;
}

interface UsageResponse {
  /** POS-I232/I234: every module and unlimited records (live trial or ended trial never converted) */
  trialAccess?: boolean;
  trialPhase?: 'live' | 'grace' | 'expired' | null;
  graceEndsAt?: string | null;
  subscription:
    | {
        plan?: { name?: string; currency?: string };
        estimatedBill?: number;
        status?: string;
        trialEndsAt?: string | null;
        addons?: Array<{ addonId: string; quantity?: number }>;
      }
    | null;
  usage: {
    users: UsageMetric;
    products: UsageMetric;
    locations: UsageMetric;
    promotions: UsageMetric;
    /** POS-I264 */
    mobileLinks?: UsageMetric;
    mobileSessions?: UsageMetric;
    sales: { last30Days: number; allTime: number };
  };
}

interface BasketPreview {
  currentBill: number;
  estimatedBill: number;
  currentLimits: Record<string, number | null | undefined> | null;
  limits: Record<string, number | null | undefined>;
  trialAccess?: boolean;
}

interface CatalogAddon {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  priceMonthly: string | number;
  currency?: string;
  type: string;
  unit?: string | null;
  limitType?: string | null;
  limitPerUnit?: number | null;
  permissionKeys?: string[] | null;
  isActive?: boolean;
  /** T-448 — third-party usage is billed by the provider. */
  isPassThrough?: boolean;
  providerName?: string | null;
}

interface CatalogPlan {
  id: string;
  name: string;
  priceMonthly: string | number;
  currency?: string;
}

interface CatalogResponse {
  plans: CatalogPlan[];
  addons: CatalogAddon[];
  currentPlanId: string | null;
  activeAddonIds: string[];
}

interface ChangeRequest {
  id: string;
  kind: 'ADDON' | 'PLAN';
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  quantity: number;
  note?: string | null;
  reviewNote?: string | null;
  createdAt: string;
  addon?: { name: string } | null;
  plan?: { name: string } | null;
}

const STATUS_STYLE: Record<string, string> = {
  PENDING: 'bg-warning/10 text-warning-text',
  APPROVED: 'bg-success/10 text-success-text',
  REJECTED: 'bg-destructive/10 text-destructive-text',
  CANCELLED: 'bg-muted text-muted-foreground',
};

function UsageBar({ label, metric }: { label: string; metric: UsageMetric }) {
  const { t } = useTranslation('settings');
  const unlimited = metric.max === null;
  const blocked = metric.max === 0;
  const pct = metric.pct ?? 0;
  const barColor = blocked
    ? 'bg-muted-foreground/40'
    : pct >= 90
      ? 'bg-destructive'
      : pct >= 75
        ? 'bg-warning'
        : 'bg-success';
  return (
    <div className="rounded-xl border p-3.5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xs text-muted-foreground">
          <bdi dir="ltr">
            {metric.used}
            {unlimited ? ' / ∞' : blocked ? '' : ` / ${metric.max}`}
          </bdi>
          {blocked && ` ${t('usageNotIncluded')}`}
        </span>
      </div>
      {!unlimited && (
        <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
          <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${blocked ? 100 : pct}%` }} />
        </div>
      )}
      {unlimited && <p className="text-xs text-success-text">{t('includesUnlimited')}</p>}
    </div>
  );
}

export function MyPlanUsageCard() {
  const { t } = useTranslation('settings');
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canRequest = hasPermission('settings:update');
  const queryClient = useQueryClient();

  const [requestOpen, setRequestOpen] = useState(false);
  // POS-I266: the basket - add-on id -> quantity requested (several add-ons in one go)
  const [basket, setBasket] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [note, setNote] = useState('');
  const [cancelTarget, setCancelTarget] = useState<string | null>(null);

  const { data: usage, isLoading, isError: usageError } = useQuery<UsageResponse>({
    queryKey: ['my-subscription-usage'],
    queryFn: () => api.get('/subscription/me').then((r) => r.data),
    staleTime: 30_000,
  });

  const { data: catalog } = useQuery<CatalogResponse>({
    queryKey: ['my-subscription-catalog'],
    queryFn: () => api.get('/subscription/catalog').then((r) => r.data),
    staleTime: 60_000,
  });

  const { data: requests } = useQuery<ChangeRequest[]>({
    queryKey: ['my-subscription-requests'],
    queryFn: () => api.get('/subscription/requests').then((r) => r.data),
    staleTime: 30_000,
  });

  const basketLines = useMemo(() => toAddonQuantities(basket).sort((a, b) => a.addonId.localeCompare(b.addonId)), [basket]);
  const liveKey = JSON.stringify(basketLines);
  const previewKey = useDebouncedValue(liveKey, 250);
  const previewQuery = useQuery<BasketPreview>({
    queryKey: ['my-subscription-preview', previewKey],
    queryFn: () => api.post('/subscription/preview', { addonQuantities: JSON.parse(previewKey) }).then((r) => r.data),
    enabled: requestOpen,
    placeholderData: (prev) => prev,
  });
  const preview = previewQuery.data;

  /** One POST /subscription/requests per line; each line reports its own success or failure. */
  const submitBasket = async () => {
    setSubmitting(true);
    const failed: string[] = [];
    const remaining: Record<string, number> = { ...basket };
    for (const line of basketLines) {
      const addon = (catalog?.addons ?? []).find((a) => a.id === line.addonId);
      const name = addon ? addonLabel(addon.key, addon.name) : line.addonId;
      try {
        await api.post('/subscription/requests', { kind: 'ADDON', addonId: line.addonId, quantity: line.quantity, note: note || undefined });
        delete remaining[line.addonId];
        toast.success(t('basketLineSent', { name }));
      } catch (e) {
        failed.push(name);
        notify.error(e, t('basketLineFailed', { name }));
      }
    }
    setBasket(remaining);
    setSubmitting(false);
    queryClient.invalidateQueries({ queryKey: ['my-subscription-requests'] });
    if (failed.length === 0) {
      setRequestOpen(false);
      setNote('');
    }
  };

  const cancelRequest = useMutation({
    mutationFn: (id: string) => api.post(`/subscription/requests/${id}/cancel`).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('requestCancelledToast'));
      setCancelTarget(null);
      queryClient.invalidateQueries({ queryKey: ['my-subscription-requests'] });
    },
    onError: (e) => notify.error(e, t('requestCancelError')),
  });

  const currency = usage?.subscription?.plan?.currency ?? 'PKR';
  const catalogAddons = catalog?.addons ?? [];
  // quantities the business owns now (the usage response has them; the catalog only lists the ids)
  const savedQty: Record<string, number> = {};
  for (const id of catalog?.activeAddonIds ?? []) savedQty[id] = 1;
  for (const a of usage?.subscription?.addons ?? []) savedQty[a.addonId] = a.quantity ?? 1;
  const usedByLimit: Record<string, number | undefined> = {
    maxUsers: usage?.usage.users.used,
    maxProducts: usage?.usage.products.used,
    maxLocations: usage?.usage.locations.used,
    maxPromotions: usage?.usage.promotions.used,
    maxMobileLinks: usage?.usage.mobileLinks?.used,
    maxMobileSessions: usage?.usage.mobileSessions?.used,
  };
  const nameOf = (id: string) => { const a = catalogAddons.find((x) => x.id === id); return a ? addonLabel(a.key, a.name) : id; };
  const discardBasket = () => { setDiscardOpen(false); setRequestOpen(false); setBasket({}); setNote(''); };
  // unsaved basket: ask before closing
  const closeBasket = () => { if (submitting) return; if (Object.keys(basket).length > 0) setDiscardOpen(true); else discardBasket(); };
  const ownedAddons = (catalog?.addons ?? []).filter((a) => catalog?.activeAddonIds.includes(a.id));
  const trial = getTrialPhaseState(
    usage?.subscription?.status,
    usage?.subscription?.trialEndsAt,
    new Date(),
    { trialPhase: usage?.trialPhase, graceEndsAt: usage?.graceEndsAt },
  );
  const trialLabel = trial
    ? trial.phase === 'expired'
      ? t('trialBadgeViewOnly')
      : trial.phase === 'grace'
        ? trial.graceEndsAt
          ? t('trialBadgeGrace', { date: formatDateOnly(trial.graceEndsAt) })
          : t('trialBadgeExpired')
        : trial.daysLeft === 0
          ? t('trialBadgeEndsToday')
          : t('trialBadgeDaysLeft', { count: trial.daysLeft })
    : null;
  const trialEnded = !!trial && trial.phase !== 'live';

  return (
    <Card id="my-plan" className="animate-fade-in-up hover:shadow-glow-sm transition-all duration-200 lg:col-span-2 scroll-mt-28">
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <CardTitle className="flex flex-wrap items-center gap-x-2 gap-y-1 text-lg">
              <Gauge className="h-5 w-5 shrink-0 text-primary-text" />
              <span className="min-w-0">{t('planUsageTitle')}</span>
              {trial && trialLabel && (
                <StatusBadge
                  status="TRIAL"
                  solid
                  tone={trial.phase === 'expired' ? 'danger' : trialEnded || trial.daysLeft <= 3 ? 'warning' : 'info'}
                  label={trialLabel}
                  icon={<Clock className="h-3 w-3" />}
                />
              )}
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {usage?.subscription?.plan?.name ? (
                <>
                  {t('currentPlanLabel')} <span className="font-semibold text-foreground">{planLabel(usage.subscription.plan.name)}</span>
                  {usage.subscription.estimatedBill != null && (
                    <> · {t('estimatedMonthlyLabel')} <span className="font-medium text-foreground">{formatCurrency(Number(usage.subscription.estimatedBill), currency)}</span></>
                  )}
                </>
              ) : (
                t('noActiveSubscription')
              )}
              {trial && usage?.subscription?.trialEndsAt && (
                <> · {t(trialEnded ? 'trialEndedOn' : 'trialEndsOn', { date: formatDateOnly(usage.subscription.trialEndsAt) })}</>
              )}
            </p>
            {usage?.trialAccess && (
              <p className="mt-1 text-sm text-success-text">{t('trialAllIncluded')}</p>
            )}
          </div>
          {canRequest && (
            <Button size="sm" className="shrink-0 gap-2 self-start" onClick={() => setRequestOpen((v) => !v)}>
              <PlusCircle className="h-4 w-4" />
              {t('requestAddonBtn')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {isLoading ? (
          <SkeletonTable rows={2} cols={2} />
        ) : usageError || !usage?.usage ? (
          <AlertBanner tone="danger">{t('planUsageUnavailable')}</AlertBanner>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            <UsageBar label={t('includesUsers')} metric={usage.usage.users} />
            <UsageBar label={t('includesProducts')} metric={usage.usage.products} />
            <UsageBar label={t('includesLocations')} metric={usage.usage.locations} />
            <UsageBar label={t('includesPromotions')} metric={usage.usage.promotions} />
            {usage.usage.mobileLinks && <UsageBar label={t('includesMobileLinks')} metric={usage.usage.mobileLinks} />}
            {usage.usage.mobileSessions && <UsageBar label={t('includesMobileSessions')} metric={usage.usage.mobileSessions} />}
            <StatCard
              label={t('salesLast30Label')}
              value={usage.usage.sales.last30Days}
              format="number"
              helper={t('salesAllTime', { value: usage.usage.sales.allTime.toLocaleString() })}
            />
          </div>
        )}

        {/* Owned add-ons — inclusions + pass-through disclosure (T-448 / T-449) */}
        {ownedAddons.length > 0 && (
          <div>
            <p className="text-sm font-medium mb-2">{t('yourAddonsTitle')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {ownedAddons.map((a) => (
                <div key={a.id} className="rounded-xl border p-3.5 space-y-2">
                  <p className="text-sm font-medium">{addonLabel(a.key, a.name)}</p>
                  <IncludedFeatures kind="addon" addon={a} max={4} />
                  {a.isPassThrough && <PassThroughNote providerName={a.providerName} />}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My requests */}
        {(requests?.length ?? 0) > 0 && (
          <div>
            <p className="text-sm font-medium mb-2">{t('myRequestsTitle')}</p>
            <div className="space-y-2">
              {requests!.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {r.kind === 'ADDON' ? (r.addon?.name ? addonLabel(null, r.addon.name) : t('addonFallback')) : (r.plan?.name ? planLabel(r.plan.name) : t('planLabel'))}
                      {r.quantity > 1 && <span className="text-muted-foreground"> ×{r.quantity}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatDate(r.createdAt)}{r.reviewNote ? ` · ${r.reviewNote}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[r.status]}`}>
                      {r.status === 'PENDING' && <Clock className="h-3 w-3" />}
                      {r.status === 'APPROVED' && <Check className="h-3 w-3" />}
                      {r.status === 'REJECTED' && <X className="h-3 w-3" />}
                      {t(`reqStatus_${r.status}`)}
                    </span>
                    {r.status === 'PENDING' && canRequest && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setCancelTarget(r.id)}>
                        {t('cancel')}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {/* POS-I266: request basket - several add-ons, quantities, server price preview */}
      {requestOpen && canRequest && (
        <FormDialog
          open
          size="full"
          title={t('requestNewAddonTitle')}
          subtitle={t('basketHelp')}
          icon={<PlusCircle className="h-5 w-5 text-primary-text" />}
          submitLabel={t('basketSubmit', { count: basketLines.length })}
          submitDisabled={basketLines.length === 0}
          isPending={submitting}
          onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); if (basketLines.length > 0 && !submitting) void submitBasket(); }}
          onClose={closeBasket}
        >
          <AddonPicker
            addons={catalogAddons}
            draft={basket}
            saved={savedQty}
            mode="add"
            currentLimits={preview?.currentLimits}
            currency={currency}
            disabled={submitting}
            loading={!catalog}
            onChange={(id, qty) => setBasket((prev) => { const next = { ...prev }; if (qty <= 0) delete next[id]; else next[id] = qty; return next; })}
            summary={
              <AddonSummary
                currency={currency}
                currentMonthly={preview?.currentBill ?? usage?.subscription?.estimatedBill}
                newMonthly={preview?.estimatedBill}
                calculating={previewKey !== liveKey || previewQuery.isFetching}
                previewError={previewQuery.isError}
                changes={diffSelection({}, basket)}
                nameOf={nameOf}
                currentLimits={preview?.currentLimits}
                newLimits={preview?.limits}
                trialAccess={preview?.trialAccess}
                usageRows={Object.entries(usedByLimit).filter(([, v]) => v !== undefined).map(([limitKey, used]) => ({ limitKey, used: used as number }))}
                warnings={usageWarnings(preview?.limits, usedByLimit)}
              >
                <div className="space-y-2 border-t pt-3">
                  <p className="text-xs text-muted-foreground">{t('basketNoteHelp')}</p>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} placeholder={t('optionalNotePlaceholder')} />
                </div>
              </AddonSummary>
            }
          />
        </FormDialog>
      )}

      <ConfirmDialog
        open={discardOpen}
        title={t('addonDiscardTitle')}
        message={t('addonDiscardMsg')}
        confirmLabel={t('addonDiscardConfirm')}
        variant="warning"
        onConfirm={discardBasket}
        onCancel={() => setDiscardOpen(false)}
      />

      <ConfirmDialog
        open={!!cancelTarget}
        title={t('cancelRequestTitle')}
        message={t('cancelRequestMsg')}
        confirmLabel={t('cancelRequestConfirm')}
        variant="warning"
        loading={cancelRequest.isPending}
        onConfirm={() => cancelTarget && cancelRequest.mutate(cancelTarget)}
        onCancel={() => setCancelTarget(null)}
      />
    </Card>
  );
}
